import { useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Check, AlertTriangle, Loader2, FileSpreadsheet, Users, ArrowRight } from "lucide-react";
import { toast } from "sonner";

const KLASS_META = {
  NEW: { l: "Nuovo", c: "bg-emerald-100 text-emerald-800", action: "create" },
  MATCHED_UPDATE: { l: "Da aggiornare", c: "bg-blue-100 text-blue-800", action: "update" },
  UNCHANGED: { l: "Già presente", c: "bg-zinc-100 text-zinc-600", action: "skip" },
  REVIEW: { l: "Da verificare", c: "bg-amber-100 text-amber-800", action: "skip" },
  POSSIBLE_DUPLICATE: { l: "Da verificare", c: "bg-amber-100 text-amber-800", action: "skip" },
  ARCHIVED_MATCH: { l: "Da verificare · archiviato", c: "bg-amber-100 text-amber-800", action: "skip" },
  INCOMPLETE: { l: "Errore", c: "bg-red-100 text-red-700", action: "skip" },
};
const VERIFY_KLASSES = ["REVIEW", "POSSIBLE_DUPLICATE", "ARCHIVED_MATCH"];

export default function GoleeImportDialog({ open, onOpenChange, onDone, cspGroups = [] }) {
  const [phase, setPhase] = useState("upload"); // upload / mapping / preview / done
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [decisions, setDecisions] = useState({});
  const [groupChoices, setGroupChoices] = useState({}); // row_id -> { label_norm: csp_group_id }
  const [mapDraft, setMapDraft] = useState({}); // label_norm -> { resolution, csp_group_ids }
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setPhase("upload"); setFile(null); setAnalysis(null);
    setDecisions({}); setGroupChoices({}); setMapDraft({});
  };

  const groupName = (id) => cspGroups.find((g) => g.id === id)?.name || id;

  const counts = useMemo(() => {
    const rows = analysis?.rows || [];
    const n = (f) => rows.filter(f).length;
    return {
      nuovo: n((r) => r.klass === "NEW"), presente: n((r) => r.klass === "UNCHANGED"), aggiornare: n((r) => r.klass === "MATCHED_UPDATE"),
      verificare: n((r) => VERIFY_KLASSES.includes(r.klass)), errore: n((r) => r.klass === "INCOMPLETE"),
      selezionate: rows.filter((r) => (decisions[r.row_id] || "skip") !== "skip" && decisions[r.row_id] !== "ignore").length,
    };
  }, [analysis, decisions]);

  const importOnlyNew = () => {
    const decs = {};
    (analysis?.rows || []).forEach((r) => { decs[r.row_id] = r.klass === "NEW" ? "create" : "skip"; });
    setDecisions(decs);
    toast.success(`Selezionati solo i ${counts.nuovo} nuovi sicuri`);
  };
  const toggleRow = (r) => setDecisions((s) => {
    const cur = s[r.row_id] || "skip";
    return { ...s, [r.row_id]: cur !== "skip" ? "skip" : ((KLASS_META[r.klass] || {}).action === "skip" ? (r.klass === "ARCHIVED_MATCH" ? "reactivate" : "skip") : KLASS_META[r.klass].action) };
  });

  const unresolvedLabels = useMemo(() => {
    if (!analysis) return [];
    return (analysis.group_resolutions || []).filter((r) => r.needs_admin);
  }, [analysis]);

  const doAnalyze = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/golee/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      const decs = {};
      (data.rows || []).forEach((r) => { decs[r.row_id] = (KLASS_META[r.klass] || {}).action || "skip"; });
      setDecisions(decs);
      const draft = {};
      (data.group_resolutions || []).forEach((r) => {
        draft[r.label_norm] = {
          resolution: r.resolution.replace("suggest_", ""),
          csp_group_ids: r.csp_group_ids || [],
        };
      });
      setMapDraft(draft);
      const needsMapping = (data.group_resolutions || []).some((r) => r.needs_admin);
      setPhase(needsMapping ? "mapping" : "preview");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore analisi"); }
    finally { setBusy(false); }
  };

  const saveMappingsAndReanalyze = async () => {
    setBusy(true);
    try {
      for (const r of unresolvedLabels) {
        const d = mapDraft[r.label_norm];
        if (!d || d.resolution === "unresolved") continue;
        await api.put("/import/golee/group-mappings", {
          label: r.label, resolution: d.resolution, csp_group_ids: d.csp_group_ids || [],
        });
      }
      // re-analyze so mappings apply
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/import/golee/analyze", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAnalysis(data);
      const decs = {};
      (data.rows || []).forEach((r) => { decs[r.row_id] = (KLASS_META[r.klass] || {}).action || "skip"; });
      setDecisions(decs);
      setPhase("preview");
      toast.success("Mapping gruppi salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore salvataggio mapping"); }
    finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      const body = {
        session_id: analysis.session_id,
        decisions: (analysis.rows || []).map((r) => ({
          row_id: r.row_id,
          action: decisions[r.row_id] || "skip",
          group_choices: groupChoices[r.row_id] || {},
        })),
      };
      const { data } = await api.post("/import/golee/confirm", body);
      setAnalysis({ ...analysis, summary: data.summary });
      setPhase("done");
      onDone?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore conferma"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="golee-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5" />Import nativo Golee</DialogTitle>
          <DialogDescription>
            Identità via Codice Fiscale · UPSERT sicuro con anteprima · mai distruttivo.
            Reimportare lo stesso file non crea duplicati.
          </DialogDescription>
        </DialogHeader>

        {phase === "upload" && (
          <div className="space-y-4">
            <input type="file" accept=".xlsx,.xls" data-testid="golee-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-black file:text-white hover:file:bg-zinc-800" />
            <p className="text-xs text-zinc-500">Carica l'export Excel nativo Golee (colonne: Cognome, Nome, Email, Telefono, Data di Nascita, Codice fiscale, Visita Medica, Gruppi, Stato tesseramento, Sesso).</p>
            <div className="flex justify-end">
              <Button data-testid="golee-analyze-btn" onClick={doAnalyze} disabled={!file || busy} className="bg-black hover:bg-zinc-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Analisi…</> : <><Sparkles className="w-4 h-4 mr-2" />Analizza</>}
              </Button>
            </div>
          </div>
        )}

        {phase === "mapping" && (
          <div className="space-y-4" data-testid="golee-mapping">
            <div className="text-sm text-zinc-600 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600" />
              Alcune etichette gruppo Golee vanno mappate ai gruppi tecnici CSP. La scelta viene salvata e riutilizzata.
            </div>
            <div className="border border-zinc-200 rounded-md divide-y">
              {unresolvedLabels.map((r) => {
                const d = mapDraft[r.label_norm] || { resolution: "unresolved", csp_group_ids: [] };
                return (
                  <div key={r.label_norm} className="p-3 space-y-2" data-testid={`map-${r.label_norm}`}>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium bg-zinc-100 px-2 py-0.5 rounded">{r.label}</span>
                      <ArrowRight className="w-4 h-4 text-zinc-400" />
                      <Select value={d.resolution} onValueChange={(v) => setMapDraft((s) => ({ ...s, [r.label_norm]: { ...d, resolution: v, csp_group_ids: v === "unique" ? (d.csp_group_ids?.slice(0, 1) || []) : d.csp_group_ids } }))}>
                        <SelectTrigger data-testid={`map-reso-${r.label_norm}`} className="h-8 text-xs w-56"><SelectValue placeholder="Scegli…" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unique">Associa a UN gruppo CSP</SelectItem>
                          <SelectItem value="ambiguous">Ambiguo (scelta per atleta)</SelectItem>
                          <SelectItem value="info_only">Solo info Golee (nessun gruppo)</SelectItem>
                          <SelectItem value="ignore">Ignora</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {d.resolution === "unique" && (
                      <Select value={d.csp_group_ids?.[0] || ""} onValueChange={(v) => setMapDraft((s) => ({ ...s, [r.label_norm]: { ...d, csp_group_ids: [v] } }))}>
                        <SelectTrigger data-testid={`map-group-${r.label_norm}`} className="h-8 text-xs w-72"><SelectValue placeholder="Gruppo CSP…" /></SelectTrigger>
                        <SelectContent>
                          {cspGroups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {d.resolution === "ambiguous" && (
                      <div className="flex flex-wrap gap-2">
                        {cspGroups.map((g) => {
                          const on = (d.csp_group_ids || []).includes(g.id);
                          return (
                            <button key={g.id} type="button"
                              onClick={() => setMapDraft((s) => {
                                const ids = new Set(d.csp_group_ids || []);
                                on ? ids.delete(g.id) : ids.add(g.id);
                                return { ...s, [r.label_norm]: { ...d, csp_group_ids: [...ids] } };
                              })}
                              className={`text-xs px-2 py-1 rounded border ${on ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300"}`}>
                              {g.name}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={reset} disabled={busy}>Ricomincia</Button>
              <Button data-testid="golee-save-mapping-btn" onClick={saveMappingsAndReanalyze} disabled={busy} className="bg-black hover:bg-zinc-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio…</> : <>Salva mapping e continua</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === "preview" && analysis && (
          <div className="space-y-4" data-testid="golee-preview">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-xs" data-testid="golee-counters">
              <Stat l="Nuovo" v={counts.nuovo} tone="emerald" />
              <Stat l="Già presente" v={counts.presente} tone="zinc" />
              <Stat l="Da aggiornare" v={counts.aggiornare} tone="blue" />
              <Stat l="Da verificare" v={counts.verificare} tone="amber" />
              <Stat l="Errore" v={counts.errore} tone="red" />
              <Stat l="Selezionate" v={counts.selezionate} tone="zinc" />
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[11px] text-zinc-500">{(analysis.missing_in_file || []).length} atleti CSP con CF non presenti nel file (solo segnalazione) · {analysis.total_rows} righe</div>
              <Button size="sm" variant="outline" data-testid="golee-import-only-new" onClick={importOnlyNew} disabled={counts.nuovo === 0}>
                Importa solo nuovi ({counts.nuovo})
              </Button>
            </div>

            <div className="border border-zinc-200 rounded-md max-h-[46vh] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 sticky top-0">
                  <tr>
                    <th className="px-2 py-2 w-8"></th>
                    <th className="text-left px-3 py-2">Stato</th>
                    <th className="text-left px-3 py-2">Atleta (file)</th>
                    <th className="text-left px-3 py-2">Motivo / Differenze / Gruppi</th>
                    <th className="text-left px-3 py-2 w-44">Azione</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.rows.map((r) => {
                    const m = KLASS_META[r.klass] || { l: r.klass, c: "bg-zinc-100" };
                    const selected = (decisions[r.row_id] || "skip") !== "skip" && decisions[r.row_id] !== "ignore";
                    return (
                      <tr key={r.row_id} data-testid={`golee-row-${r.row_id}`} className={`border-t border-zinc-100 align-top ${selected ? "bg-emerald-50/40" : ""}`}>
                        <td className="px-2 py-2"><input type="checkbox" data-testid={`golee-select-${r.row_id}`} checked={selected} onChange={() => toggleRow(r)} disabled={r.klass === "INCOMPLETE"} className="w-4 h-4 accent-black" /></td>
                        <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${m.c}`}>{m.l}</span></td>
                        <td className="px-3 py-2">
                          <div className="font-medium">{r.file_data.first_name} {r.file_data.last_name}</div>
                          <div className="text-zinc-500 font-mono">{r.file_data.codice_fiscale || "—"}</div>
                          {r.reason && <div className="text-[11px] text-amber-800 mt-1" data-testid={`golee-reason-${r.row_id}`}>{r.reason}</div>}
                          {(r.errors || []).map((e, i) => <div key={i} className="text-[11px] text-red-700 mt-1">{typeof e === "string" ? e : e.message || JSON.stringify(e)}</div>)}
                          <div className="text-zinc-400 font-mono">{r.file_data.birth_date || "—"} · {r.file_data.gender || "—"}</div>
                        </td>
                        <td className="px-3 py-2 space-y-1">
                          {(r.diffs || []).map((d, i) => (
                            <div key={i} className="text-[11px]">
                              <span className="font-mono text-zinc-500">{d.field}:</span>{" "}
                              <span className={d.state === "cf_conflict" ? "text-red-700 font-semibold" : d.state === "update" ? "text-amber-700" : "text-emerald-700"}>
                                {d.state === "will_fill" ? "vuoto" : String(d.db)} → {String(d.file)}
                              </span>
                              {d.state === "cf_conflict" && <span className="ml-1 text-red-600">(CF diverso — non sovrascritto)</span>}
                            </div>
                          ))}
                          {(r.file_data.golee_groups || []).length > 0 && (
                            <div className="text-[11px] text-zinc-500">Golee: {r.file_data.golee_groups.join(", ")}</div>
                          )}
                          {(r.group_adds || []).length > 0 && (
                            <div className="text-[11px] text-emerald-700">+ gruppi CSP: {r.group_adds.map(groupName).join(", ")}</div>
                          )}
                          {(r.group_removed || []).length > 0 && (
                            <div className="text-[11px] text-amber-700">membership Golee rimossa: {r.group_removed.join(", ")} (nessuna cancellazione automatica)</div>
                          )}
                          {(r.group_review || []).map((gr, i) => (
                            <div key={i} className="text-[11px] text-amber-800 flex items-center gap-1 flex-wrap">
                              <AlertTriangle className="w-3 h-3" />{gr.label} → scegli:
                              <Select value={(groupChoices[r.row_id] || {})[gr.label_norm] || ""}
                                onValueChange={(v) => setGroupChoices((s) => ({ ...s, [r.row_id]: { ...(s[r.row_id] || {}), [gr.label_norm]: v } }))}>
                                <SelectTrigger data-testid={`golee-groupchoice-${r.row_id}-${gr.label_norm}`} className="h-6 text-[11px] w-40"><SelectValue placeholder="gruppo CSP" /></SelectTrigger>
                                <SelectContent>
                                  {(gr.candidates || []).map((cid, j) => <SelectItem key={cid} value={cid}>{gr.candidate_names?.[j] || groupName(cid)}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          ))}
                        </td>
                        <td className="px-3 py-2">
                          <Select value={decisions[r.row_id] || "skip"} onValueChange={(v) => setDecisions((s) => ({ ...s, [r.row_id]: v }))}>
                            <SelectTrigger data-testid={`golee-action-${r.row_id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {r.klass === "NEW" && <SelectItem value="create">Crea</SelectItem>}
                              {r.klass === "ARCHIVED_MATCH" && <SelectItem value="reactivate">Riattiva (stesso ID)</SelectItem>}
                              {r.matched_athlete && r.klass !== "ARCHIVED_MATCH" && r.klass !== "REVIEW" && <SelectItem value="update">Aggiorna</SelectItem>}
                              <SelectItem value="skip">Salta</SelectItem>
                              <SelectItem value="ignore">Ignora</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {(analysis.missing_in_file || []).length > 0 && (
              <div className="text-[11px] text-zinc-500 border border-zinc-200 rounded p-2">
                <Users className="w-3 h-3 inline mr-1" />
                {analysis.missing_in_file.length} atleti CSP con Codice Fiscale non presenti in questo file (nessuna azione, solo segnalazione).
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={reset} disabled={busy}>Ricomincia</Button>
              <Button data-testid="golee-confirm-btn" onClick={doConfirm} disabled={busy} className="bg-black hover:bg-zinc-800">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio…</> : <><Check className="w-4 h-4 mr-2" />Conferma e salva</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === "done" && analysis?.summary && (
          <div className="space-y-3" data-testid="golee-done">
            <div className="text-center py-4">
              <Check className="w-10 h-10 mx-auto text-emerald-600 mb-2" />
              <h3 className="font-heading font-semibold text-xl">Import Golee completato</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <Stat l="Creati" v={analysis.summary.created} tone="emerald" />
              <Stat l="Riattivati" v={analysis.summary.reactivated || 0} tone="emerald" />
              <Stat l="Creazioni bloccate" v={analysis.summary.blocked_create || 0} tone="amber" />
              <Stat l="Aggiornati" v={analysis.summary.updated} tone="blue" />
              <Stat l="Campi completati" v={analysis.summary.fields_filled} tone="emerald" />
              <Stat l="Gruppi aggiunti" v={analysis.summary.groups_added} tone="blue" />
              <Stat l="Invariati" v={analysis.summary.unchanged} tone="zinc" />
              <Stat l="Saltati" v={analysis.summary.skipped} tone="zinc" />
              <Stat l="Ignorati" v={analysis.summary.ignored} tone="zinc" />
              <Stat l="Errori" v={(analysis.summary.errors || []).length} tone="amber" />
            </div>
            <DialogFooter>
              <Button onClick={() => { reset(); onOpenChange(false); }} className="bg-black hover:bg-zinc-800">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ l, v, tone }) {
  const c = { emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    red: "bg-red-50 text-red-700 border-red-200",
    zinc: "bg-zinc-50 text-zinc-700 border-zinc-200" };
  return (
    <div className={`border rounded p-2 ${c[tone] || c.zinc}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{l}</div>
      <div className="text-lg font-heading font-bold">{v ?? 0}</div>
    </div>
  );
}
